import { format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  useTrackAddedShareLinkExpirationDate,
  useTrackAddedShareLinkPasscode,
  useTrackGeneratedShareUrl,
} from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';
import { resetSelectedItemsAction } from '@air/redux-selected-items';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type ShareCustomColors } from '~/components/ShareModal/components/ShareDesignColorRow';
import { type CustomFontsInput, type CustomLogoInput } from '~/components/ShareModal/types/presentationSettingsInputs';
import { useCreateCollectionMutation } from '~/components/ShowcaseModal/hooks/mutations/useCreateCollectionMutation';
import {
  type CoverImageInput,
  type PermissionMode,
  type ShortUrlProps,
  type ShowcaseCustomFonts,
  type ShowcaseCustomLogo,
  type ShowcaseProps,
} from '~/components/ShowcaseModal/types';
import { validateShortUrlProps } from '~/components/ShowcaseModal/utils/validation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getShortUrlUpdatePresentationSettings } from '~/swr-hooks/shortUrl/getShortUrlUpdatePresentationSettings';
import { type ShortUrlDefaultSettingsResponse } from '~/swr-hooks/shortUrl/useGetDefaultSettings';
import {
  buildDownloadPermissionShortUrlUpdate,
  type DownloadPermissionMode,
} from '~/utils/shortUrl/buildDownloadPermissionShortUrlUpdate';
import { buildMetadataVisibilityShortUrlUpdate } from '~/utils/shortUrl/buildMetadataVisibilityShortUrlUpdate';
import { buildShareUrlGeneratedAnalyticsParams } from '~/utils/shortUrl/buildShareUrlGeneratedAnalyticsParams';

export type ShowcaseCreated = {
  collectionId: string;
  /** The showcase's short URL record, so the entry point can build links and analytics params. */
  shortUrl: ShortUrlResponse;
};

export type UseCreateShowcaseModalStateParams = {
  assetIds: string[];
  boardIds: string[];
  /**
   * Hands the new showcase to the entry point that opened the modal, which owns what happens next:
   * the Showcases page takes the user straight into the editor, while the library share menu and the
   * selection bar leave the user where they were and show a confirmation toast.
   */
  onShowcaseCreated: (created: ShowcaseCreated) => void;
  permissions: ShortUrlProps['permissions'];
  /**
   * The workspace's stored share-link presentation defaults (FOUND-133). The design settings the
   * defaults author (colors, logo, font) seed the modal's draft once resolved, so the Design tab
   * shows the actual starting point of the link being created — the server applies these same
   * defaults to a create payload that omits them, which used to leave the tab claiming the system
   * look while the created showcase came out branded.
   */
  defaultPresentationSettings: ShortUrlDefaultSettingsResponse['presentationSettings'];
  /**
   * Whether the defaults query has settled (success OR error). The seed below latches on this, not
   * on the data: an errored query also renders the modal, and without the latch a later successful
   * refetch would deliver the defaults for the first time mid-session and clobber the user's
   * in-progress design.
   */
  areDefaultSettingsSettled: boolean;
};

/**
 * Resolves a seeded branding key's create-payload update: the drafted value when one is set; an
 * explicit null when a workspace default was seeded and the user removed it (an omitted key would
 * let the server apply that same default right back); undefined — omit the key — when there was
 * never a default to clear, exactly as before seeding existed.
 */
const getDraftBrandingUpdate = <Value>(draft: Value | null, storedDefault: unknown): Value | null | undefined =>
  draft ?? (storedDefault ? null : undefined);

export { getDraftBrandingUpdate };

export const DEFAULT_PERMISSIONS: ShortUrlProps['permissions'] = {
  asset: {
    canViewOriginal: false,
    canViewAllVersions: false,
    canManageDiscussions: false,
    canViewDiscussions: false,
  },
  customField: {
    canView: false,
    canManageApplications: false,
  },
  canvas: {
    canViewChatHistory: false,
  },
};

export const useCreateShowcaseModalState = ({
  assetIds,
  boardIds,
  onShowcaseCreated,
  permissions,
  defaultPresentationSettings,
  areDefaultSettingsSettled,
}: UseCreateShowcaseModalStateParams) => {
  const dispatch = useDispatch();
  const { trackAddedShareLinkExpirationDate } = useTrackAddedShareLinkExpirationDate();
  const { trackAddedShareLinkPasscode } = useTrackAddedShareLinkPasscode();
  const { currentWorkspace } = useCurrentWorkspace();
  const { trackGeneratedShareUrl } = useTrackGeneratedShareUrl();
  const createMutation = useCreateCollectionMutation();

  const [showcaseProps, setShowcaseProps] = useState<ShowcaseProps>({
    name: format(new Date(), 'MMM d, yyyy'),
    description: '',
  });
  const [shortUrlProps, setShortUrlProps] = useState<ShortUrlProps>({
    disabled: false,
    passcode: null,
    expirationDate: null,
    presentationSettings: undefined,
    permissions,
  });

  /**
   * Image branding is held outside `shortUrlProps.presentationSettings` and folded into the create
   * payload at submit time. Each value is a read | write union: a freshly picked asset is a write
   * Input with no resolved `url` (the tab previews it from the picked thumbnail), while a seeded
   * workspace default is the stored read shape whose resolved `url` IS the preview. The payload
   * normalizer strips the read-only fields either way.
   */
  const [customLogo, setCustomLogo] = useState<ShowcaseCustomLogo | CustomLogoInput | null>(null);
  const [coverImage, setCoverImage] = useState<CoverImageInput | null>(null);
  // Same read | write union as the logo: a picked built-in font is a write Input, a seeded default
  // is the read shape carrying the resolved `fontFamily` the tab displays.
  const [customFonts, setCustomFonts] = useState<ShowcaseCustomFonts | CustomFontsInput | null>(null);

  const [validationErrors, setValidationErrors] = useState<Record<string, string> | null>(null);

  // Install the workspace's default permissions once they resolve — the modal usually mounts before
  // the defaultSettings query lands (it waits on the current workspace), so the useState initializer
  // above captured the built-in defaults only. Update ONLY the permissions field: this effect used to
  // re-run the full initial state (name, passcode, branding, …) on every new `permissions` identity,
  // which wiped in-progress edits whenever the query resolved or refetched mid-session — the user's
  // typed link name snapped back to today's date, and every context consumer re-rendered at once
  // (the CreateShowcaseModal spec's long-standing flake).
  useEffect(() => {
    setShortUrlProps((prev) => ({ ...prev, permissions }));
  }, [permissions]);

  /**
   * Seed the design draft from the workspace defaults exactly once, on the query's FIRST settle.
   * On the success path the modal renders nothing until then, so the seed can never race a user
   * edit. Latching on settle (not on data) matters for the error path: the modal renders there too,
   * and a later successful refetch must never seed mid-session over the user's in-progress design.
   */
  const hasSeededDesignDefaultsRef = useRef(false);
  /**
   * Snapshot of `defaultPresentationSettings` at the query's first settle. `getDraftBrandingUpdate`
   * must read this, not the live prop: a first-settle error skips seeding, but a later refetch can
   * populate the prop — treating that as "was seeded" would send explicit nulls and block the server
   * from applying defaults the Design tab never showed.
   */
  const seededPresentationDefaultsRef = useRef(defaultPresentationSettings);

  useEffect(() => {
    if (hasSeededDesignDefaultsRef.current || !areDefaultSettingsSettled) {
      return;
    }

    hasSeededDesignDefaultsRef.current = true;
    seededPresentationDefaultsRef.current = defaultPresentationSettings;

    if (!defaultPresentationSettings) {
      return;
    }

    const {
      customColors: defaultColors,
      customLogo: defaultLogo,
      customFonts: defaultFonts,
    } = defaultPresentationSettings;

    // An empty stored customColors means "no color defaults" — seeding {} would still send an
    // explicit empty object at create, needlessly overriding whatever the server would apply.
    if (defaultColors && Object.keys(defaultColors).length > 0) {
      setShortUrlProps((prev) => ({
        ...prev,
        presentationSettings: { ...prev.presentationSettings, customColors: defaultColors },
      }));
    }

    if (defaultLogo) {
      setCustomLogo(defaultLogo);
    }

    if (defaultFonts) {
      setCustomFonts(defaultFonts);
    }
  }, [areDefaultSettingsSettled, defaultPresentationSettings]);

  useEffect(() => {
    let isCancelled = false;

    const validate = async () => {
      const validation = await validateShortUrlProps({
        name: showcaseProps.name,
        description: showcaseProps.description ?? null,
        disabled: shortUrlProps.disabled,
        passcode: shortUrlProps.passcode,
        expirationDate: shortUrlProps.expirationDate,
        presentationSettings: shortUrlProps.presentationSettings,
        permissions: shortUrlProps.permissions,
      });

      if (!isCancelled) {
        setValidationErrors(validation.errors);
      }
    };

    void validate();

    return () => {
      isCancelled = true;
    };
  }, [shortUrlProps, showcaseProps]);

  const updateShowcaseName = useCallback((name: string) => {
    setShowcaseProps((prev) => ({ ...prev, name }));
  }, []);

  const updateShowcaseDescription = useCallback((description: string) => {
    setShowcaseProps((prev) => ({ ...prev, description }));
  }, []);

  const updateAssetPermission = useCallback(
    <K extends keyof ShortUrlProps['permissions']['asset']>(
      key: K,
      value: ShortUrlProps['permissions']['asset'][K],
    ) => {
      setShortUrlProps((prev) => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          asset: {
            ...prev.permissions.asset,
            [key]: value,
          },
        },
      }));
    },
    [],
  );

  const updateCommentPermissions = useCallback((mode: PermissionMode) => {
    const canViewDiscussions = mode === 'read-only' || mode === 'allow';
    const canManageDiscussions = mode === 'allow';

    setShortUrlProps((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        asset: {
          ...prev.permissions.asset,
          canViewDiscussions,
          canManageDiscussions,
        },
      },
    }));
  }, []);

  const updateCustomFieldPermissions = useCallback((mode: PermissionMode) => {
    const canView = mode === 'read-only' || mode === 'allow';
    const canManageApplications = mode === 'allow';

    setShortUrlProps((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        customField: {
          ...prev.permissions.customField,
          canView,
          canManageApplications,
        },
      },
    }));
  }, []);

  const updateDownloadPermissions = useCallback((mode: DownloadPermissionMode) => {
    setShortUrlProps((prev) => {
      const update = buildDownloadPermissionShortUrlUpdate(mode);

      return {
        ...prev,
        // Local state must accumulate all settings across separate toggle updates
        // so other fields (e.g. hideInfoTab) keep their values until save.
        presentationSettings: {
          ...prev.presentationSettings,
          ...update.presentationSettings,
        },
        permissions: {
          ...prev.permissions,
          asset: {
            ...prev.permissions.asset,
            ...update.permissions.asset,
          },
        },
      };
    });
  }, []);

  const updateMetadataVisibility = useCallback((showMetadata: boolean) => {
    setShortUrlProps((prev) => {
      const update = buildMetadataVisibilityShortUrlUpdate(showMetadata, prev.presentationSettings);

      return {
        ...prev,
        presentationSettings: update.presentationSettings,
        ...(update.permissions
          ? {
              permissions: {
                ...prev.permissions,
                customField: {
                  ...prev.permissions.customField,
                  ...update.permissions.customField,
                },
              },
            }
          : {}),
      };
    });
  }, []);

  const updateHideSearchBar = useCallback((hideSearchBar: boolean) => {
    setShortUrlProps((prev) => ({
      ...prev,
      presentationSettings: {
        ...prev.presentationSettings,
        hideSearchBar,
      },
    }));
  }, []);

  const updateHideAirLogo = useCallback((hideAirLogo: boolean) => {
    setShortUrlProps((prev) => ({
      ...prev,
      presentationSettings: {
        ...prev.presentationSettings,
        hideAirLogo,
      },
    }));
  }, []);

  const updateCustomColors = useCallback((customColors: ShareCustomColors | undefined) => {
    setShortUrlProps((prev) => ({
      ...prev,
      presentationSettings: {
        ...prev.presentationSettings,
        customColors,
      },
    }));
  }, []);

  const updateCustomLogo = useCallback((nextCustomLogo: CustomLogoInput | null) => {
    setCustomLogo(nextCustomLogo);
  }, []);

  const updateCoverImage = useCallback((nextCoverImage: CoverImageInput | null) => {
    setCoverImage(nextCoverImage);
  }, []);

  const updateCustomFonts = useCallback((nextCustomFonts: CustomFontsInput | null) => {
    setCustomFonts(nextCustomFonts);
  }, []);

  const updateLockedControlsExpandNested = useCallback((expandNested: boolean) => {
    setShortUrlProps((prev) => ({
      ...prev,
      presentationSettings: {
        ...prev.presentationSettings,
        lockedControls: {
          ...prev.presentationSettings?.lockedControls,
          expandNested,
        },
      },
    }));
  }, []);

  const updatePasscode = useCallback((passcode: string | null) => {
    setShortUrlProps((prev) => ({ ...prev, passcode }));
  }, []);

  const updateExpirationDate = useCallback((expirationDate: string | null) => {
    setShortUrlProps((prev) => ({ ...prev, expirationDate }));
  }, []);

  const updateLinkAccessPermission = useCallback((enabled: boolean) => {
    setShortUrlProps((prev) => ({ ...prev, disabled: !enabled }));
  }, []);

  const handleCreate = useCallback(async (): Promise<void> => {
    const validation = await validateShortUrlProps({
      name: showcaseProps.name,
      description: showcaseProps.description ?? null,
      disabled: shortUrlProps.disabled,
      passcode: shortUrlProps.passcode,
      expirationDate: shortUrlProps.expirationDate,
      presentationSettings: shortUrlProps.presentationSettings,
      permissions: shortUrlProps.permissions,
    });

    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    if (!currentWorkspace?.id) {
      throw new Error('No workspace id');
    }

    // Fold the drafted branding (held outside presentationSettings) into the create payload —
    // getDraftBrandingUpdate turns a removed seeded default into an explicit null clear.
    const customLogoUpdate = getDraftBrandingUpdate(customLogo, seededPresentationDefaultsRef.current?.customLogo);
    const customFontsUpdate = getDraftBrandingUpdate(customFonts, seededPresentationDefaultsRef.current?.customFonts);

    const mergedPresentationSettings = {
      ...validation.data.presentationSettings,
      ...(customLogoUpdate !== undefined ? { customLogo: customLogoUpdate } : {}),
      ...(coverImage ? { coverImage } : {}),
      ...(customFontsUpdate !== undefined ? { customFonts: customFontsUpdate } : {}),
    };

    // Always normalize when anything is keyed: seeded values are read shapes whose server-resolved
    // fields (`url`, contextSource `hex`/`fontFamily`) the write API rejects. An empty merge keeps
    // the undefined/null the validated draft carried, so an untouched modal still omits the field.
    const presentationSettings =
      Object.keys(mergedPresentationSettings).length > 0
        ? getShortUrlUpdatePresentationSettings(mergedPresentationSettings)
        : validation.data.presentationSettings;

    createMutation.mutate(
      {
        workspaceId: currentWorkspace.id,
        collection: {
          name: showcaseProps.name,
          description: showcaseProps.description || undefined,
          assetIds,
          boardIds,
        },
        shortUrl: { ...validation.data, presentationSettings },
      },
      {
        onSuccess: ({ collection, shortUrl }) => {
          if (shortUrl.passcode) {
            trackAddedShareLinkPasscode({ shortId: shortUrl.id ?? '' });
          }
          if (shortUrl.expirationDate) {
            trackAddedShareLinkExpirationDate({ shortId: shortUrl.id ?? '' });
          }

          trackGeneratedShareUrl(
            buildShareUrlGeneratedAnalyticsParams({
              shareId: shortUrl.id,
              shortUrl,
              objectType: 'collection',
            }),
          );

          const collectionId = 'id' in collection ? collection.id : undefined;

          // A created share link without a collection id means creation didn't actually produce a
          // usable link — handing it to the entry point (whose navigation and copy-tracking both
          // need the id) would be lying. Report it instead; the wire contract says this can't happen.
          if (!collectionId) {
            reportErrorToBugsnag({
              error: new Error('Collection share link created without a collection id'),
              context: 'useCreateShowcaseModalState',
              metadata: { data: { shortUrlId: shortUrl.id } },
            });
          } else {
            onShowcaseCreated({ collectionId, shortUrl });
          }

          dispatch(resetSelectedItemsAction());
        },
      },
    );
  }, [
    showcaseProps,
    shortUrlProps,
    customLogo,
    coverImage,
    customFonts,
    createMutation,
    trackAddedShareLinkPasscode,
    trackAddedShareLinkExpirationDate,
    assetIds,
    boardIds,
    onShowcaseCreated,
    dispatch,
    currentWorkspace?.id,
    trackGeneratedShareUrl,
  ]);

  return {
    showcaseProps: {
      name: showcaseProps.name,
      description: showcaseProps.description,
    },
    shortUrlProps: {
      disabled: shortUrlProps.disabled,
      passcode: shortUrlProps.passcode,
      expirationDate: shortUrlProps.expirationDate,
      presentationSettings: shortUrlProps.presentationSettings,
      permissions: shortUrlProps.permissions,
    },
    isSaving: createMutation.isPending,
    /** Latches once creation succeeds, so hosts can swap to their post-create UI and nudges. */
    hasCreatedShowcase: createMutation.isSuccess,
    validationErrors,
    // Draft branding held outside `shortUrlProps.presentationSettings` (read | write unions — a
    // pick is a write Input, a seeded workspace default the stored read shape), surfaced so the
    // Design tab can display the drafted font/logo and gate value-dependent UI (e.g. the image
    // buttons' "Remove image" dropdown). A picked logo/cover previews from transient tab state; a
    // seeded one from its read shape's resolved `url`.
    draftCustomFonts: customFonts,
    draftCustomLogo: customLogo,
    draftCoverImage: coverImage,
    updateShowcaseName,
    updateShowcaseDescription,
    updateLinkAccessPermission,
    updateAssetPermission,
    updateCommentPermissions,
    updateCustomFieldPermissions,
    updateDownloadPermissions,
    updateMetadataVisibility,
    updateHideSearchBar,
    updateHideAirLogo,
    updateCustomColors,
    updateCustomLogo,
    updateCoverImage,
    updateCustomFonts,
    updateLockedControlsExpandNested,
    updatePasscode,
    updateExpirationDate,
    handleCreate,
    selectedItemCount: assetIds.length + boardIds.length,
  };
};
